import Vue from '@front-app/vendor/vue';
import {
	markRaw,
	ref,
	Ref,
} from '@vue/composition-api';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import Underline from '@tiptap/extension-underline';
import Paragraph from '@tiptap/extension-paragraph';
import StarterKit from '@tiptap/starter-kit';
import { StringTranslator } from '@front-app/hooks/useTranslate';
import { AnyExtension, Editor } from '@tiptap/vue-2';
import { UserSearchItem } from '@front-app/services/UserApi/UserModel';
import { onRemoved } from '../../hooks/onRemoved';
import { TrLink as Link } from './extensions/Link';
import { TrAlignment as Alignment } from './extensions/Alignment';
import { LINK } from './marks';
import { atMentionSuggestionConfig, TrAtMention } from './extensions/AtMention';
import { MentionProviderFunction, MentionSelectFunction } from './extensions/AtMentionModel';
import { validateLink } from './extensions/link/util';

export const EMPTY_MESSAGE = '<p></p>';

export interface UseTiptap {
	editor: Editor;
	selectMention: MentionSelectFunction;
	mentionItems: Ref<UserSearchItem[]>;
	mentionListRef: Ref<Vue | Element | null>;
}

export interface EditorOptions {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	atMentionConfig?: {
		translationService: StringTranslator;
		mentionProvider: MentionProviderFunction;
	};
}

export function useTiptapEditor(el: Ref<Vue|Element|null>, options: EditorOptions = {}): UseTiptap {
	const extensions: AnyExtension[] = [
		Underline,
		StarterKit, // Includes bold, italic, and list extensions among others
		Link.configure({
			trAutolink: true, // Custom auto-linking function
			autolink: false, // Also disable the standard autolinking
			linkOnPaste: true,
			openOnClick: false,
			validate: validateLink,
		}),
		Alignment.configure({
			types: [Paragraph.name],
		}),
		BubbleMenu.configure({
			// Only show the editor for Links
			shouldShow: ({ editor }) => editor.isActive(LINK),
		}),
	];

	const mentionListRef = ref(null);
	let mentionItems: Ref<UserSearchItem[]> = ref([]);
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	let selectMention: MentionSelectFunction = () => {};

	if (options.atMentionConfig) {
		const suggestionData = atMentionSuggestionConfig({
			translationService: options.atMentionConfig.translationService,
			mentionProvider: options.atMentionConfig.mentionProvider,
			mentionListRef,
		});
		mentionItems = suggestionData.mentionItems;
		selectMention = suggestionData.selectMention;
		extensions.push(TrAtMention.configure({
			suggestion: suggestionData.config,
			HTMLAttributes: {
				class: 'mention',
			},
			renderLabel(data) {
				const {
					options: renderOptions,
					node,
				} = data;
				return `${renderOptions.suggestion.char}${node.attrs.label ?? node.attrs.id}`;
			},
		}));
	}
	const editor = markRaw(new Editor({
		content: '',
		extensions,
	}));

	onRemoved(el, () => {
		editor.destroy();
	});

	return {
		editor,
		selectMention,
		mentionItems,
		mentionListRef,
	};
}
